import { yupResolver } from '@hookform/resolvers/yup'
import { TFunction } from 'i18next'
import { useEffect } from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ObjectSchema, object, string } from 'yup'
import * as yup from 'yup'

import { PasswordRecoveryContract } from '@/Account/application/PasswordRecovery'
import { PasswordRecoveryRepository } from '@/Account/domain/PasswordRecoveryRepository'
import {
  dniOrCifSchema,
  emailSchema,
  nonEmptyStringSchema,
  useMetaResponseHandler,
  validateMetaResponse,
} from '@/Shared/ui/form'
import { useLoader } from '@/Shared/ui/loader'
import { openNavigationRoutes } from '@/Shared/ui/routers'
import { formTranslation } from '@/Shared/ui/sharedTranslation'

import { LoginTranslation } from '../accountTranslation'

export interface PasswordRecoveryData {
  email: string
  merchantId: string
  legalId: string
}

export interface ResetPasswordData {
  token: string
  password: string
  passwordRepeat: string
}

export interface TokenData {
  token: string
  userName: string
}

const schema = (
  t: TFunction<'translation', undefined>,
): ObjectSchema<PasswordRecoveryData> =>
  object({
    email: emailSchema(t, true).required(),
    merchantId: nonEmptyStringSchema(t)
      .min(4, t(formTranslation.errors.minChars, { value: 4 }))
      .matches(/^\d{4,}$/, t(formTranslation.errors.invalidmerchantIdFormat))
      .required(),
    legalId: string().required(t(formTranslation.errors.fieldRequired)),
  })

const resetPasswordSchema = (
  t: TFunction<'translation', undefined>,
): ObjectSchema<ResetPasswordData> =>
  object({
    token: string().required(),
    password: nonEmptyStringSchema(t)
      .min(8, t(formTranslation.errors.minChars, { value: 8 }))
      .matches(/^\S+$/, t(formTranslation.errors.noValidFormat))
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/,
        t(formTranslation.errors.invalidPasswordFormat),
      )
      .required(),
    passwordRepeat: nonEmptyStringSchema(t)
      .oneOf([yup.ref('password')], t(formTranslation.errors.passwordsDoNotMatch))
      .required(),
  })

export interface IUsePasswordRecoveryController {
  form: UseFormReturn<PasswordRecoveryData>
  resetPasswordForm: UseFormReturn<ResetPasswordData>
  verifyIdentity: (data: PasswordRecoveryData) => Promise<boolean>
  resendEmail: (data: PasswordRecoveryData) => Promise<string>
  updatePassword: (data: ResetPasswordData, userName: string) => Promise<boolean>
  validateToken: (data: TokenData) => Promise<boolean>
}

export const usePasswordRecoveryController = (
  repository: PasswordRecoveryRepository,
): IUsePasswordRecoveryController => {
  const { t } = useTranslation()
  const { startLoading, stopLoading } = useLoader()
  const navigate = useNavigate()
  const { handleMetaResponse } = useMetaResponseHandler()

  const form = useForm<PasswordRecoveryData>({
    resolver: yupResolver(schema(t)),
    mode: 'onBlur',
  })

  const resetPasswordForm = useForm<ResetPasswordData>({
    resolver: yupResolver(resetPasswordSchema(t)),
    mode: 'onBlur',
  })

  const watchPassword = resetPasswordForm.watch('password')
  useEffect(() => {
    if (resetPasswordForm.getValues('passwordRepeat')) {
      resetPasswordForm.setValue('passwordRepeat', '', {
        shouldValidate: false,
        shouldDirty: true,
      })
    }
  }, [watchPassword])

  const verifyIdentity = async (data: PasswordRecoveryData): Promise<boolean> => {
    startLoading()

    const response = await PasswordRecoveryContract(repository).verifyIdentity(data)

    stopLoading()

    const valid = validateMetaResponse(response?.meta)

    if (valid) {
      navigate(openNavigationRoutes.passwordRecoveryEmailSuccess, {
        state: {
          fromPasswordRecovery: true,
          email: data.email,
          merchantId: data.merchantId,
          legalId: data.legalId,
        },
      })
    }

    return valid
  }

  const resendEmail = async (data: PasswordRecoveryData): Promise<string> => {
    startLoading()

    const response = await PasswordRecoveryContract(repository).resendEmail(data)

    stopLoading()

    const valid = handleMetaResponse(response?.meta, undefined, {
      notifySuccess: true,
      successMessage: t(LoginTranslation.recovery.form.resetPassword.resendMailToast),
    })

    return valid ? response.data ?? '' : ''
  }

  const updatePassword = async (
    data: ResetPasswordData,
    userName: string,
  ): Promise<boolean> => {
    startLoading()
    const response = await PasswordRecoveryContract(repository).updatePassword(
      data,
      userName,
    )
    stopLoading()

    const valid = validateMetaResponse(response?.meta)

    if (valid) {
      navigate(openNavigationRoutes.passwordRecoveryPasswordChangeSuccess, {
        state: {
          fromChangePassword: true,
        },
      })
    } else {
      if (response?.meta?.messages[0]?.description) {
        resetPasswordForm.setError('root', {
          type: 'custom',
          message: response?.meta?.messages[0].description,
        })
      } else {
        navigate(openNavigationRoutes.passwordRecoveryPasswordChangeError, {
          state: {
            fromChangePassword: true,
          },
        })
      }
    }

    return valid
  }

  const validateToken = async (data: TokenData): Promise<boolean> => {
    startLoading()
    const response = await PasswordRecoveryContract(repository).validateToken(data)
    stopLoading()

    return !!response
  }

  return {
    form,
    resetPasswordForm,
    verifyIdentity,
    resendEmail,
    updatePassword,
    validateToken,
  }
}
